import { PersistedWorkflow } from '@cotera/sdk/core';
import type { ArtifactPanelViewEntry } from '@cotera/client/v0/chat/artifact-panel-context';
import type { DrillTarget } from './workflow-drill-context';
import type { WorkflowNodeViewData } from './components';
import {
  DEFAULT_WORKFLOW_RENDER_CATALOG,
  resolveGraphNodeRenderableNodeByViewData,
  resolveTriggerRenderableNode,
} from './workflow-render-catalog';

export const WORKFLOW_RUN_VIEW_TYPE = 'workflow-run' as const;

export type WorkflowRunViewPayload = {
  type: typeof WORKFLOW_RUN_VIEW_TYPE;
  workflowId: string;
  runId: string;
};

export const WORKFLOW_DRILL_VIEW_TYPE = 'workflow-drill' as const;

export type WorkflowDrillViewPayload = {
  type: typeof WORKFLOW_DRILL_VIEW_TYPE;
  workflowId: string;
  target: NonNullable<DrillTarget>;
};

export const WORKFLOW_AGENT_VIEW_TYPE = 'workflow-agent' as const;

export type WorkflowAgentViewPayload = {
  type: typeof WORKFLOW_AGENT_VIEW_TYPE;
  workflowId: string;
  agentId: string;
  agentName?: string;
};

/**
 * The stack entry for an agent a workflow node points at.
 *
 * Shared because two node editors open the same thing: an agent node's own
 * agent, and the agent a for-each node runs its loop body with. Built here for
 * the reason {@link workflowDatasetViewEntry} is — the id stays one string, so
 * the two cannot mint `workflow-agent-${id}` differently and end up stacking a
 * second entry for the agent already on screen.
 *
 * A blank name is the same as no name, in the title and in the payload alike:
 * the two used to disagree, and an agent whose name was `''` got an entry with
 * no title at all.
 */
export function workflowAgentViewEntry(params: {
  workflowId: string;
  agentId: string;
  agentName?: string;
}): ArtifactPanelViewEntry {
  const { workflowId, agentId } = params;
  const agentName =
    params.agentName !== undefined && params.agentName !== ''
      ? params.agentName
      : null;
  return {
    id: `workflow-agent-${agentId}`,
    title: agentName ?? 'Agent',
    payload: {
      type: WORKFLOW_AGENT_VIEW_TYPE,
      workflowId,
      agentId,
      ...(agentName !== null && { agentName }),
    },
  };
}

export const WORKFLOW_GRAPH_VIEW_TYPE = 'workflow-graph' as const;

export type WorkflowGraphViewPayload = {
  type: typeof WORKFLOW_GRAPH_VIEW_TYPE;
  workflowId: string;
  targetWorkflowId: string;
  targetWorkflowName?: string;
};

export const WORKFLOW_TREE_VIEW_TYPE = 'workflow-tree' as const;

/**
 * What this view is called, everywhere it is named.
 *
 * One constant because the name shows up in four places — the toolbar button, the
 * panel breadcrumb, the shell breadcrumb, and the view's own heading — and two of
 * those breadcrumbs come from different sources (the artifact panel renders stack
 * entry titles, the workflow shell builds its own). Renaming it as four string
 * literals means renaming three of them.
 */
export const WORKFLOW_TREE_VIEW_TITLE = 'Hierarchy';

/**
 * The whole nested-workflow hierarchy for one workflow, in one graph.
 *
 * Carries only the workflow it was opened from: the tree is resolved from that
 * workflow's live graph (so unsaved `run-workflow` nodes are in it) plus a
 * recursive fetch of whatever they point at.
 */
export type WorkflowTreeViewPayload = {
  type: typeof WORKFLOW_TREE_VIEW_TYPE;
  workflowId: string;
};

export const WORKFLOW_DATASET_VIEW_TYPE = 'workflow-dataset' as const;

/**
 * The dataset a dataset trigger watches, drilled into from the trigger node.
 *
 * Editing a dataset trigger means looking at its rows, not at a config form —
 * so the trigger's inspector is a thin surface whose "Open dataset" button
 * pushes this, rather than a config drawer.
 */
export type WorkflowDatasetViewPayload = {
  type: typeof WORKFLOW_DATASET_VIEW_TYPE;
  workflowId: string;
  datasetId: string;
  datasetName?: string;
};

/**
 * The stack entry for a dataset trigger's dataset.
 *
 * Shared because two affordances open the same thing: the "Open dataset" button
 * in the trigger's inspector, and "Test trigger" in the toolbar — testing a
 * dataset trigger means picking rows and running them, which is the dataset
 * page's own Run control.
 * Built here so the id stays one string; two call sites minting
 * `workflow-dataset-${id}` separately is how one of them ends up pushing a
 * second stack entry for the dataset already on screen.
 */
export function workflowDatasetViewEntry(params: {
  workflowId: string;
  datasetId: string;
  datasetName: string;
  fallbackTitle: string;
}): ArtifactPanelViewEntry {
  const { workflowId, datasetId, datasetName, fallbackTitle } = params;
  return {
    id: `workflow-dataset-${datasetId}`,
    title: datasetName !== '' ? datasetName : fallbackTitle,
    payload: {
      type: WORKFLOW_DATASET_VIEW_TYPE,
      workflowId,
      datasetId,
      ...(datasetName !== '' && { datasetName }),
    },
  };
}

export const WORKFLOW_RUN_STEP_VIEW_TYPE = 'workflow-run-step' as const;

/** A single run graph node's step detail, pushed onto the artifact stack. */
export type WorkflowRunStepViewPayload = {
  type: typeof WORKFLOW_RUN_STEP_VIEW_TYPE;
  workflowId: string;
  runId: string;
  nodeId: string;
  /** When set, step views preselect this for-each / fan-out instance. */
  instanceKey?: string;
};

const { WorkflowNodeType } = PersistedWorkflow;

export function isAgentWorkflowNode(node: WorkflowNodeViewData): boolean {
  return node.type === WorkflowNodeType.Agent;
}

export function getWorkflowDrillTargetTitle(
  target: NonNullable<DrillTarget>
): string {
  const catalog = DEFAULT_WORKFLOW_RENDER_CATALOG;
  if (target.kind === 'node') {
    return resolveGraphNodeRenderableNodeByViewData(
      catalog,
      target.node
    ).view.getTitle(target.node);
  }
  return resolveTriggerRenderableNode(
    catalog,
    target.data.trigger
  ).view.getTitle(target.data.trigger);
}

export function isWorkflowRunViewPayload(
  payload: unknown
): payload is WorkflowRunViewPayload {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  return (
    record['type'] === WORKFLOW_RUN_VIEW_TYPE &&
    typeof record['workflowId'] === 'string' &&
    typeof record['runId'] === 'string'
  );
}

export function isWorkflowDrillViewPayload(
  payload: unknown
): payload is WorkflowDrillViewPayload {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  if (
    record['type'] !== WORKFLOW_DRILL_VIEW_TYPE ||
    typeof record['workflowId'] !== 'string'
  ) {
    return false;
  }
  const target = record['target'];
  if (target === null || typeof target !== 'object') {
    return false;
  }
  const targetRecord = target as Record<string, unknown>;
  const kind = targetRecord['kind'];
  return kind === 'node' || kind === 'trigger';
}

export function isWorkflowAgentViewPayload(
  payload: unknown
): payload is WorkflowAgentViewPayload {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  return (
    record['type'] === WORKFLOW_AGENT_VIEW_TYPE &&
    typeof record['workflowId'] === 'string' &&
    typeof record['agentId'] === 'string'
  );
}

export function isWorkflowGraphViewPayload(
  payload: unknown
): payload is WorkflowGraphViewPayload {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  return (
    record['type'] === WORKFLOW_GRAPH_VIEW_TYPE &&
    typeof record['workflowId'] === 'string' &&
    typeof record['targetWorkflowId'] === 'string'
  );
}

export function isWorkflowTreeViewPayload(
  payload: unknown
): payload is WorkflowTreeViewPayload {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  return (
    record['type'] === WORKFLOW_TREE_VIEW_TYPE &&
    typeof record['workflowId'] === 'string'
  );
}

/**
 * The stack entry for a workflow's nested-workflow tree.
 *
 * Titled for the view rather than the workflow: the panel already puts the
 * workflow's name in the breadcrumb ahead of this, so repeating it here would
 * read as "Workflow / Workflow / Hierarchy".
 */
export function workflowTreeViewEntry(params: {
  workflowId: string;
}): ArtifactPanelViewEntry {
  return {
    id: `workflow-tree-${params.workflowId}`,
    title: WORKFLOW_TREE_VIEW_TITLE,
    payload: {
      type: WORKFLOW_TREE_VIEW_TYPE,
      workflowId: params.workflowId,
    },
  };
}

export function isWorkflowDatasetViewPayload(
  payload: unknown
): payload is WorkflowDatasetViewPayload {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  return (
    record['type'] === WORKFLOW_DATASET_VIEW_TYPE &&
    typeof record['workflowId'] === 'string' &&
    typeof record['datasetId'] === 'string'
  );
}

/**
 * View types that embed a whole other resource page rather than a panel of this
 * one. They bring their own breadcrumbs, back affordance and actions, so the
 * workflow shell hands the chrome over instead of stacking its own on top.
 *
 * Add a view here when it renders a resource page; the shell needs no other
 * change.
 */
const SELF_CHROMED_VIEW_TYPES: ReadonlySet<string> = new Set([
  WORKFLOW_AGENT_VIEW_TYPE,
  WORKFLOW_DATASET_VIEW_TYPE,
]);

/** Whether `payload` renders its own page chrome — see {@link SELF_CHROMED_VIEW_TYPES}. */
export function viewOwnsPageChrome(payload: unknown): boolean {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const type = (payload as Record<string, unknown>)['type'];
  return typeof type === 'string' && SELF_CHROMED_VIEW_TYPES.has(type);
}

export function isWorkflowRunStepViewPayload(
  payload: unknown
): payload is WorkflowRunStepViewPayload {
  if (payload === null || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  return (
    record['type'] === WORKFLOW_RUN_STEP_VIEW_TYPE &&
    typeof record['workflowId'] === 'string' &&
    typeof record['runId'] === 'string' &&
    typeof record['nodeId'] === 'string'
  );
}
